// src/pages/EditProduct.jsx
import React, { useState, useEffect } from 'react';
import styles from './EditProduct.module.css';
import masaLogo from '../Assets/MasaLogo.png';
import { getProducts, updateProduct, deleteProduct } from '../api/productApi';

const EditProduct = () => {
  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmDeleteSKU, setConfirmDeleteSKU] = useState(null);

  useEffect(() => {
    getProducts()
      .then((response) => setProducts(response.data))
      .catch((error) => {
        console.error('Error fetching products:', error);
        setErrorMessage('Failed to fetch products. Please try again.');
      });
  }, []);

  const handleEditClick = (product) => {
    const productCopy = {
      ...product,
      sorts: product.sorts.map((sort) => ({
        ...sort,
        colors: sort.colors.map((color) => ({ ...color })),
      })),
      imageFile: null,
    };
    setEditingProduct(productCopy);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, JPG, PNG, and GIF files are allowed.');
        e.target.value = null;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5 MB.');
        e.target.value = null;
        return;
      }
      setEditingProduct({ ...editingProduct, imageFile: file });
    } else {
      setEditingProduct({ ...editingProduct, imageFile: null });
    }
  };

  const handleSortChange = (index, field, value) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[index][field] = value;
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleAddSort = () => {
    setEditingProduct({
      ...editingProduct,
      sorts: [...editingProduct.sorts, { sortName: '', colors: [{ color: '', quantity: 0 }] }],
    });
  };

  const handleRemoveSort = (index) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts.splice(index, 1);
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleColorChange = (sortIndex, colorIndex, field, value) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors[colorIndex][field] =
      field === 'quantity' ? parseInt(value, 10) || 0 : value;
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleAddColor = (sortIndex) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors.push({ color: '', quantity: 0 });
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const handleRemoveColor = (sortIndex, colorIndex) => {
    const updatedSorts = [...editingProduct.sorts];
    updatedSorts[sortIndex].colors.splice(colorIndex, 1);
    setEditingProduct({ ...editingProduct, sorts: updatedSorts });
  };

  const validateForm = () => {
    if (!editingProduct.name.trim() || !editingProduct.size.trim() || !editingProduct.SKU.trim()) {
      setErrorMessage('Name, size, and SKU are required.');
      return false;
    }
    for (let sort of editingProduct.sorts) {
      if (!sort.sortName.trim()) {
        setErrorMessage('All sorts must have a valid name.');
        return false;
      }
      for (let color of sort.colors) {
        if (!color.color.trim()) {
          setErrorMessage('All colors must have a valid name.');
          return false;
        }
      }
    }
    return true;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) return;
    const { _id, name, size, SKU, sorts, imageFile } = editingProduct;
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('size', size);
      formData.append('SKU', SKU);
      formData.append('sorts', JSON.stringify(sorts));
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const updated = await updateProduct(_id, formData);
      setProducts((prev) => prev.map((p) => (p._id === _id ? updated.data : p)));
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      setErrorMessage('Failed to update product. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleCloseModal = () => {
    setErrorMessage(null);
  };

  const handleDeleteRequest = (sku) => {
    setConfirmDeleteSKU(sku);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteSKU) return;
    try {
      await deleteProduct(confirmDeleteSKU);
      setProducts((prev) => prev.filter((product) => product.SKU !== confirmDeleteSKU));
    } catch (error) {
      console.error('Error deleting product:', error);
      setErrorMessage('Failed to delete product. Please try again.');
    }
    setConfirmDeleteSKU(null);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteSKU(null);
  };

  const getImageSrc = (image) => {
    if (image && image.data && image.contentType) {
      try {
        const byteArray = Array.isArray(image.data.data) ? image.data.data : [];
        const binaryString = byteArray.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        const base64String = window.btoa(binaryString);
        return `data:${image.contentType};base64,${base64String}`;
      } catch (error) {
        console.error('Error converting image data to base64:', error);
        return '';
      }
    }
    return '';
  };

  return (
    <main className={styles.manageProductsPage}>
      <header className={styles.header}>
        <img loading="lazy" src={masaLogo} className={styles.logo} alt="Logo" />
        <h1 className={styles.title}>Manage Products</h1>
      </header>

      <div className={styles.productGrid}>
        {products.map((product) => (
          <div key={product._id} className={styles.productCard}>
            {product.image && product.image.data && product.image.contentType ? (
              <img
                src={getImageSrc(product.image)}
                alt={`Product: ${product.name}`}
                className={styles.productImage}
              />
            ) : (
              <div className={styles.placeholderImage}>No Image</div>
            )}
            <h2 className={styles.productName}>{product.name}</h2>
            <p className={styles.productDetail}>
              <strong>Size:</strong> {product.size}
            </p>
            <p className={styles.productDetail}>
              <strong>SKU:</strong> {product.SKU}
            </p>
            {product.sorts.map((sort, sortIndex) => (
              <div key={sortIndex} className={styles.sortList}>
                <h3>Sort: {sort.sortName}</h3>
                {sort.colors.map((color, colorIndex) => (
                  <p key={colorIndex}>
                    {color.color}: {color.quantity}
                  </p>
                ))}
              </div>
            ))}
            {/* Display Held Quantities */}
            {product.sorts.map((sort, sortIndex) => (
              <div key={`held-${sortIndex}`} className={styles.heldSortList}>
                <h3>Held Quantities for Sort: {sort.sortName}</h3>
                {sort.colors.map((color, colorIndex) => (
                  <p key={colorIndex}>
                    {color.color}: {color.reservedQuantity || 0}
                  </p>
                ))}
              </div>
            ))}
            <div className={styles.buttonGroup}>
              <button className={styles.editButton} onClick={() => handleEditClick(product)}>Edit</button>
              <button className={styles.deleteButton} onClick={() => handleDeleteRequest(product.SKU)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Warning</h2>
            <p>{errorMessage}</p>
            <button onClick={handleCloseModal} className={styles.modalCloseButton}>Close</button>
          </div>
        </div>
      )}

      {confirmDeleteSKU && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete the product with SKU: {confirmDeleteSKU}?</p>
            <div className={styles.modalButtonGroup}>
              <button onClick={handleDeleteConfirm} className={styles.modalConfirmButton}>Yes, Delete</button>
              <button onClick={handleDeleteCancel} className={styles.modalCancelButton}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editingProduct && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContentLarge}>
            <h2>Edit Product</h2>
            <label className={styles.formLabel}>Name:</label>
            <input
              className={styles.formInput}
              type="text"
              value={editingProduct.name}
              onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
              required
            />

            <label className={styles.formLabel}>Size:</label>
            <input
              className={styles.formInput}
              type="text"
              value={editingProduct.size}
              onChange={(e) => setEditingProduct({ ...editingProduct, size: e.target.value })}
              required
            />

            <label className={styles.formLabel}>SKU:</label>
            <input
              className={styles.formInput}
              type="text"
              value={editingProduct.SKU}
              onChange={(e) => setEditingProduct({ ...editingProduct, SKU: e.target.value })}
              required
            />

            {editingProduct.image && editingProduct.image.data && editingProduct.image.contentType ? (
              <div className={styles.currentImageContainer}>
                <h3>Current Image:</h3>
                <img
                  src={getImageSrc(editingProduct.image)}
                  alt={`Current Image of ${editingProduct.name}`}
                  className={styles.currentImage}
                />
              </div>
            ) : (
              <div className={styles.currentImageContainer}><h3>No Current Image</h3></div>
            )}

            <label className={styles.formLabel}>Update Product Image (Optional):</label>
            <input
              type="file"
              accept="image/*"
              className={styles.imageInput}
              onChange={handleImageChange}
            />

            <h3 className={styles.formHeading}>Sorts</h3>
            {editingProduct.sorts.map((sort, sortIndex) => (
              <div key={sortIndex} className={styles.sortContainer}>
                <input
                  className={styles.sortInput}
                  type="text"
                  value={sort.sortName}
                  onChange={(e) => handleSortChange(sortIndex, 'sortName', e.target.value)}
                  placeholder="Sort name"
                  required
                />
                <h4 className={styles.formHeading}>Colors & Quantities</h4>
                {sort.colors.map((color, colorIndex) => (
                  <div key={colorIndex} className={styles.colorRow}>
                    <input
                      className={styles.colorInput}
                      type="text"
                      value={color.color}
                      onChange={(e) => handleColorChange(sortIndex, colorIndex, 'color', e.target.value)}
                      placeholder="Color name"
                      required
                    />
                    <input
                      className={styles.quantityInput}
                      type="number"
                      min="0"
                      value={color.quantity}
                      onChange={(e) => handleColorChange(sortIndex, colorIndex, 'quantity', e.target.value)}
                      placeholder="Quantity"
                      required
                    />
                    {sort.colors.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeColorButton}
                        onClick={() => handleRemoveColor(sortIndex, colorIndex)}
                      >
                        Remove Color
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className={styles.addColorButton}
                  onClick={() => handleAddColor(sortIndex)}
                >
                  Add Another Color
                </button>
                {editingProduct.sorts.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeSortButton}
                    onClick={() => handleRemoveSort(sortIndex)}
                  >
                    Remove Sort
                  </button>
                )}
              </div>
            ))}
            <button type="button" className={styles.addSortButton} onClick={handleAddSort}>
              Add Another Sort
            </button>

            <div className={styles.modalButtonGroup}>
              <button onClick={handleSaveChanges} className={styles.modalConfirmButton}>Save Changes</button>
              <button onClick={handleCancelEdit} className={styles.modalCancelButton}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default EditProduct; 
